/* eslint-disable no-use-before-define */
import _ from 'underscore';
import { useRef, useCallback } from 'react';
import { useJsDataContext } from '@bingads-webui-cc-react/js-data-rest';
import { refreshJsDataCache } from '@bingads-webui-clientcenter/js-data-cc-rest-base';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { useAccountPatch, useCustomerPatch, useUserPatch } from '../use-entity';
import { useTelemetry } from '../use-telemetry';
import { PrimaryPurpose } from '../business-details-primary-use-edit';
import LifecycleEvent from '../lifecycle-event';

export default function useChangeHandlers({
  scope,
  baseline,
  requiresAgreement,
  requiresPrimaryUse,
  requiresExplicitPrimaryUse,
  initialPrimaryUse,
  onPrimaryUseSaved,
  onStateChange,
  overrideTargetCountry,
}) {
  const dataRef = useRef({
    tax: {},
    terms: {},
  });

  const updateAccount = useAccountPatch(scope);
  const updateCustomer = useCustomerPatch(scope);
  const updateUser = useUserPatch(scope);

  const jsData = useJsDataContext();

  // NOTE: customer & account will be preloaded by default after this
  // NOTE2: ignore any errors caused by this preloading, as they should be handled later
  const refreshEntitiesCache = useCallback(() => refreshJsDataCache({
    jsData,
    cid: scope.cid,
    aid: scope.aid,
    entitiesToClear: ['Account', 'Customer', 'User'],
  }).catch(_.noop), [jsData, scope.cid, scope.aid]);

  const { trace: log } = useTelemetry();

  const onValidationDone = useCallback((validationData) => {
    if (validationData.success && (!requiresAgreement || dataRef.current.terms.accepted)) {
      log('Validation done.');
      const newData = {
        ...dataRef.current,
        ...convertFormData(validationData),
      };

      dataRef.current = newData;

      let primaryUse = requiresPrimaryUse ? {
        ...dataRef.current.primaryUse,
      } : {};

      if (requiresPrimaryUse && !requiresExplicitPrimaryUse && _.isEmpty(primaryUse)) {
        primaryUse = {
          PrimaryPurpose: PrimaryPurpose.SelfAdvertising,
        };
      }

      if (requiresExplicitPrimaryUse && _.isEmpty(primaryUse) && initialPrimaryUse) {
        primaryUse = {
          PrimaryPurpose: initialPrimaryUse,
        };
      }

      if (requiresExplicitPrimaryUse) {
        log(`PrimaryUseSelected: ${JSON.stringify(primaryUse)}`);
      }

      // NOTE: we always expect to have a baseline customer and account if we're here;
      // baseline user object might be missing if certain flags (e.g. TST) are not enabled
      const customerUpdate = () => updateCustomer({
        Customer: {
          ...baseline.customer,
          Name: newData.name,
          BusinessPhone: newData.phone,
          // make sure the TargetMarket aligns with the selected business location (country)
          TargetCountry: _.isFunction(overrideTargetCountry) ? overrideTargetCountry(newData.country) : newData.country,
          Address: {
            ...baseline.customer.Address,
            ...newData.address,
          },
          ...primaryUse,
        },
      });

      const accountUpdate = () => updateAccount({
        Account: {
          ...baseline.account,
          Name: newData.name,
          CurrencyType: newData.currency,
          TaxInformation: newData.tax.id,
          TaxCertification: newData.tax.certificate,
          TimeZone: newData.timeZone,
          BusinessAddress: {
            ...baseline.account.BusinessAddress,
            ...newData.address,
            BusinessName: newData.name,
          },
        },
      });

      const userAddress = baseline.user ? {
        // NOTE: the user's address might be independent from the ABL, so don't override the country
        Address: {
          ...baseline.user.Address,
          ...newData.userAddress,
        },
      } : {};

      const userTerms = requiresAgreement ? {
        ...dataRef.current.terms.communication,
      } : {};

      let userUpdate;
      if (!_.isEmpty(userAddress) || !_.isEmpty(userTerms)) {
        userUpdate = updateUser({
          User: {
            ...(baseline.user || {}),
            ...userTerms,
            ...userAddress,
          },
        });
      } else {
        // NOOP
        userUpdate = Promise.resolve();
      }

      let allAccountUpdates = Promise.all([customerUpdate(), accountUpdate()]).catch((error) => {
        log('Failed to update customer and account.');
        return Promise.reject(error);
      });
      // NOTE: the account cannot be made active if the terms were not accepted!
      if (dataRef.current.terms.accepted) {
        // NOTE: some customer and account properties (e.g. TargetCountry, CurrencyType) can only be updated while the account is in the 'Draft' state
        allAccountUpdates = allAccountUpdates.then(() => updateAccount({
          Account: {
            LifecycleStatus: Constants.AccountLifecycleStatus.Pending,
          },
        })).catch((error) => {
          log('Failed to set the account to Pending.');
          return Promise.reject(error);
        });
      }

      Promise
        .all([allAccountUpdates, userUpdate])
        .then(refreshEntitiesCache)
        .then(() => {
          if (requiresExplicitPrimaryUse) {
            onPrimaryUseSaved();
          }
        })
        .then(() => onStateChange({ type: LifecycleEvent.DataChanged, args: { data: newData, validated: true } }))
        .then(() => onStateChange({ type: LifecycleEvent.DataSaved }))
        .catch(error => onStateChange({ type: LifecycleEvent.SaveFailed, args: { error } }));
    } else {
      log(`Validation failed. Form: ${validationData.success}, TC: ${!requiresAgreement || dataRef.current.terms.accepted}`);
      onStateChange({ type: LifecycleEvent.SaveFailed, args: { validated: false } });
    }
  }, [baseline, updateCustomer, updateAccount, updateUser, onStateChange, refreshEntitiesCache,
    requiresAgreement, requiresPrimaryUse, requiresExplicitPrimaryUse, initialPrimaryUse,
    log, onPrimaryUseSaved, overrideTargetCountry]);

  const onFormChange = useCallback((formData) => {
    const newData = {
      ...dataRef.current,
      ...convertFormData(formData),
    };

    dataRef.current = newData;
    onStateChange({ type: LifecycleEvent.DataChanged, args: { data: newData } });
  }, [onStateChange]);

  const onTermsChange = useCallback(({ accepted, communication }) => {
    const newData = {
      ...dataRef.current,
      terms: {
        accepted,
        communication,
      },
    };

    dataRef.current = newData;
    onStateChange({ type: LifecycleEvent.DataChanged, args: { data: newData } });
  }, [onStateChange]);

  const onPrimaryUseChange = useCallback((selectedPrimaryUse) => {
    const newData = {
      ...dataRef.current,
      primaryUse: {
        PrimaryPurpose: selectedPrimaryUse,
      },
    };

    dataRef.current = newData;
    onStateChange({ type: LifecycleEvent.DataChanged, args: { data: newData } });
  }, [onStateChange]);

  const checkAgreement = useCallback(() => {
    if (requiresAgreement && !dataRef.current.terms.accepted) {
      log(`Validation failed. TC: ${dataRef.current.terms.accepted}`);
      return Promise.reject();
    }

    return Promise.resolve();
  }, [log, requiresAgreement]);

  return {
    onFormChange,
    onTermsChange,
    onPrimaryUseChange,
    checkAgreement,
    onValidationDone,
  };
}

function convertFormData({
  businessNameData,
  countryCode,
  currencyData,
  phoneNumberData,
  addressData,
  userAddressData,
  taxData,
  timeZoneData,
}) {
  return {
    country: countryCode,
    currency: currencyData && currencyData.currencyCode,
    name: businessNameData && sanitizeInputText(businessNameData.businessName),
    phone: phoneNumberData && phoneNumberData.phoneNumberWithCallingCode,
    address: addressData && clearNullFields(addressData.address),
    userAddress: userAddressData && clearNullFields(userAddressData.address),
    timeZone: timeZoneData && parseInt(timeZoneData.timeZone, 10),
    tax: {
      id: taxData && taxData.data,
      certificate: taxData && taxData.taxCertification,
    },
  };
}

function sanitizeInputText(text) {
  return text && text.replace(/(\r\n|\n|\r|\t)/gm, ' ');
}

// NOTE: CCMT skips NULLs by default, so to erase empty address fields, make sure they are actually set as empty strings
const EmptyString = '';
function clearNullFields(address) {
  return _.mapObject(address, value => (value != null ? value : EmptyString));
}
